import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search as SearchIcon, Users, Flag } from "lucide-react";
import { TopBar } from "../components/TopBar";
import { AppShell, AppFooter } from "../components/Shell";
import { BottomNav } from "../components/BottomNav";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { useT, useLabels } from "../i18n";

export const HISTORY_STATUSES = ["past", "archived"];

export const raceDateLabel = (r, fmtDate, t) => {
  if (!r.date) return r.id === "other" ? "" : t ? t("race_date_tbc") : "";
  if (!r.end_date || r.end_date === r.date) return fmtDate(r.date);
  const a = new Date(r.date), b = new Date(r.end_date);
  if (a.getMonth() === b.getMonth()) return `${a.getDate()}–${fmtDate(r.end_date)}`;
  return `${fmtDate(r.date, { day: "numeric", month: "short" })} – ${fmtDate(r.end_date)}`;
};

export const RaceCard = ({ race, compact = false }) => {
  const { t, fmtDate } = useT();
  return (
    <Link to={`/courses/${race.id}`} data-testid={`race-card-${race.id}`} className="panel group flex flex-col p-4 transition-[border-color] hover:border-rox-orange">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="eyebrow">{race.city}</p>
          <h3 className="h-display mt-1 text-2xl md:text-3xl">{race.name}</h3>
        </div>
        {!["upcoming", "tbc"].includes(race.status) && <span data-testid={`race-status-${race.id}`} className="shrink-0 rounded-sm border border-rox-line px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-rox-muted">{t(`race_status_${race.status}`)}</span>}
      </div>
      <p className={`mt-2 font-display text-base uppercase tracking-wide ${race.date ? "text-rox-orange" : "text-rox-muted"}`} data-testid={`race-date-${race.id}`}>{raceDateLabel(race, fmtDate, t)}</p>
      <div className="mt-4 flex flex-col gap-1 text-sm">
        <span className="flex items-center gap-1.5" data-testid={`race-athletes-${race.id}`}><Users size={14} className="text-rox-orange" /> {t("athletes_count", race.athletes)}</span>
        <span className="flex items-center gap-1.5 text-rox-off/80" data-testid={`race-searching-${race.id}`}><Flag size={14} className="text-rox-orange" /> {t("searching_count", race.searching)}</span>
      </div>
      {!compact && <span className="mt-4 flex items-center justify-between font-display text-sm uppercase tracking-wider text-rox-off/80 group-hover:text-rox-orange">{t("race_open")} <ArrowRight size={16} /></span>}
    </Link>
  );
};

export default function Races() {
  const { user } = useAuth();
  const { t } = useT();
  const [races, setRaces] = useState(null);
  const [q, setQ] = useState("");
  const [range, setRange] = useState("all");

  useEffect(() => { api.get("/races/overview").then((r) => setRaces(r.data)).catch(() => setRaces([])); }, []);

  const { upcoming, history } = useMemo(() => {
    const key = q.trim().toLowerCase();
    const limit = range === "all" ? null : new Date(Date.now() + Number(range) * 30 * 86400000);
    const matchQ = (r) => !key || r.name.toLowerCase().includes(key) || (r.city || "").toLowerCase().includes(key);
    const all = (races || []).filter(matchQ);
    return {
      upcoming: all.filter((r) => !HISTORY_STATUSES.includes(r.status) && (!limit || (r.date && new Date(r.date) <= limit))),
      history: all.filter((r) => HISTORY_STATUSES.includes(r.status)),
    };
  }, [races, q, range]);
  const visible = upcoming;

  return (
    <AppShell>
      <TopBar back={false} />
      <main className="px-5 pb-28 pt-2 lg:px-8 lg:pb-16" data-testid="races-page">
        <h1 className="h-display text-4xl lg:text-5xl">{t("races_h1")}</h1>
        <p className="mt-2 max-w-xl text-sm text-rox-off/75">{t("races_sub")}</p>
        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          <div className="relative flex-1">
            <SearchIcon size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-rox-muted" />
            <input data-testid="races-search" className="field pl-11" placeholder={t("races_search")} value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <select data-testid="races-date-filter" className="field appearance-none sm:w-56" value={range} onChange={(e) => setRange(e.target.value)}>
            <option value="all">{t("date_all")}</option>
            <option value="3">{t("date_3m")}</option>
            <option value="6">{t("date_6m")}</option>
          </select>
        </div>
        <div className="mt-5 grid grid-cols-1 gap-3 stagger lg:grid-cols-2 xl:grid-cols-3" data-testid="races-list">
          {visible.map((r) => <RaceCard key={r.id} race={r} />)}
        </div>
        {races && visible.length === 0 && <p className="panel mt-5 p-5 text-sm text-rox-muted" data-testid="races-empty">{t("races_empty")}</p>}
        {history.length > 0 && (
          <section className="mt-10" data-testid="races-history">
            <h2 className="h-display text-2xl text-rox-off/80">{t("races_history_h")}</h2>
            <p className="mt-1 text-xs text-rox-muted">{t("races_history_sub")}</p>
            <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-3" data-testid="races-history-list">
              {history.map((r) => <RaceCard key={r.id} race={r} />)}
            </div>
          </section>
        )}
        {!user && (
          <Link to="/inscription" data-testid="races-signup-cta" className="btn-primary mt-8 lg:max-w-sm">{t("landing_cta")} <ArrowRight size={20} /></Link>
        )}
      </main>
      <AppFooter withNav={!!user} />
      {user && <BottomNav />}
    </AppShell>
  );
}
